import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    EmailAuthProvider,
    TotpMultiFactorGenerator,
    getMultiFactorResolver,
    multiFactor,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    sendEmailVerification,
} from 'firebase/auth';
import { ShieldCheck, Smartphone, KeyRound, Copy, Download, Check } from 'lucide-react';
import { auth, googleProvider } from '../../config/firebase';
import apiService from '../../services/apiService';
import { tryRefreshFirebaseToken } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n';
import { userErrorMessage } from '../../utils/userError';
import {
    PHONE_FACTOR_ID,
    TOTP_FACTOR_ID,
    downloadRecoveryCodes,
    factorLabel,
    mfaErrorMessage,
    sanitizeCode,
} from '../../utils/mfa';
import MfaChallenge from '../MfaChallenge';
import QRCode from '../QRCode';

// The SettingsModal row vocabulary (design §12), repeated here rather than
// imported: SettingsModal renders this component, so importing back from it
// would be circular. Keep in step with the constants at the top of that file.
const GROUP_CARD = 'rounded-card border border-pai bg-white/[0.025] overflow-hidden';
const ROW = 'flex items-center justify-between gap-5 px-[22px] py-[17px] border-b border-pai-hairline last:border-b-0 max-[479px]:flex-wrap max-[479px]:gap-y-3';
const ROW_MAIN = 'flex items-center gap-3 min-w-0 max-[479px]:flex-1 max-[479px]:min-w-[10rem]';
const ROW_LABEL = 'text-[15px] font-semibold text-white';
const ROW_DESC = 'mt-1 text-[13px] text-pai-meta';
const EYEBROW = 'pai-eyebrow block mb-3';
const PANEL = 'px-[22px] py-[17px] border-b border-pai-hairline last:border-b-0 space-y-4';
const INPUT = 'block w-full h-10 px-3 pai-input appearance-none focus:outline-none text-sm';
const BTN = 'flex-shrink-0 rounded-ctl border border-pai-subtle bg-white/[0.04] px-4 py-2 text-[13px] font-semibold text-pai-secondary transition-colors hover:border-pai-strong hover:text-white disabled:cursor-not-allowed disabled:opacity-40';
const BTN_PRIMARY = 'flex-shrink-0 rounded-ctl bg-white px-4 py-2 text-[13px] font-semibold text-pai-void transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/40';

const readFactors = () => {
    const user = auth.currentUser;
    if (!user) return [];
    try {
        return [...(multiFactor(user).enrolledFactors || [])];
    } catch (_) {
        return [];
    }
};

const fetchRecoveryCodes = async () => {
    const res = await apiService.post('/auth/mfa/recovery-codes', {});
    return Array.isArray(res?.codes) ? res.codes : [];
};

/**
 * The ten fresh recovery codes, shown ONCE (2FA #333). Continuing is gated on
 * the acknowledgement checkbox: a user who enables 2FA without keeping these
 * has no way back in but an operator reset.
 */
export const RecoveryCodesPanel = ({ codes, onContinue, busy = false }) => {
    const { t } = useLanguage();
    const [ack, setAck] = useState(false);
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(codes.join('\n'));
            setCopied(true);
        } catch (_) { /* clipboard blocked — Download still works */ }
    };

    return (
        <div className="space-y-3" data-testid="mfa-recovery-step">
            <div>
                <div className={ROW_LABEL}>{t('mfaCodesTitle', 'Save your recovery codes')}</div>
                <div className={ROW_DESC}>
                    {t('mfaCodesIntro', 'If you lose your phone, a recovery code is the only way back into your account. Keep them somewhere safe, away from your phone.')}
                </div>
            </div>
            <ol
                data-testid="mfa-recovery-codes"
                translate="no"
                className="grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-ctl border border-pai bg-white/[0.03] px-4 py-3 font-mono text-[14px] text-white"
            >
                {codes.map((c) => (
                    <li key={c} data-testid="mfa-recovery-code">{c}</li>
                ))}
            </ol>
            <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="mfa-recovery-copy" onClick={copy} className={`${BTN} inline-flex items-center gap-1.5`}>
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? t('mfaCodesCopied', 'Copied') : t('mfaCodesCopy', 'Copy')}
                </button>
                <button type="button" data-testid="mfa-recovery-download" onClick={() => downloadRecoveryCodes(codes, t)} className={`${BTN} inline-flex items-center gap-1.5`}>
                    <Download size={14} />
                    {t('mfaCodesDownload', 'Download (.txt)')}
                </button>
            </div>
            <label className="flex items-start gap-2.5 text-[13px] text-pai-secondary cursor-pointer">
                <input
                    type="checkbox"
                    data-testid="mfa-recovery-ack"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    className="mt-0.5"
                />
                <span>{t('mfaCodesAck', 'I have saved these 10 recovery codes. Each one works once.')}</span>
            </label>
            <div className="flex justify-end">
                <button
                    type="button"
                    data-testid="mfa-recovery-continue"
                    disabled={!ack || busy}
                    onClick={onContinue}
                    className={BTN_PRIMARY}
                >
                    {t('mfaContinue', 'Continue')}
                </button>
            </div>
        </div>
    );
};

/**
 * Settings → Security (2FA #333).
 *
 * The factors are FIREBASE's (`multiFactor(user)`): enrol / unenrol happen in
 * the client SDK, and after every change the backend is told to re-read them
 * (`POST /auth/mfa/sync`) — it is what enforces `401 mfa_required` on other
 * sessions and what owns the recovery codes. `mfa` is the profile's
 * `{enabled, recovery_codes_remaining}` block, kept fresh from each sync.
 *
 * Every enrol/unenrol may need a recent sign-in (`auth/requires-recent-login`):
 * the action is parked, the user re-authenticates (password, or Google popup),
 * and — when that re-auth itself hits the second factor — passes MfaChallenge,
 * after which the parked action runs again.
 */
const SecuritySection = ({ mfa = null, onMfaChange }) => {
    const { t } = useLanguage();
    const [factors, setFactors] = useState(readFactors);
    const [info, setInfo] = useState(mfa);
    useEffect(() => { if (mfa) setInfo(mfa); }, [mfa]);

    // { kind: 'totp'|'regen', step: 'secret'|'codes'|'code', … }
    const [wizard, setWizard] = useState(null);
    const [enrollCode, setEnrollCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // 'enabled' | 'removed' | 'verify-email' | 'codes-saved'
    const [notice, setNotice] = useState(null);

    // Re-authentication: the parked action, the password draft, and — when the
    // re-auth hits the second factor — the resolver MfaChallenge completes.
    const [reauthOpen, setReauthOpen] = useState(false);
    const [reauthPassword, setReauthPassword] = useState('');
    const [reauthError, setReauthError] = useState(null);
    const [challenge, setChallenge] = useState(null);
    const retryRef = useRef(null);

    const enabled = factors.length > 0 || info?.enabled === true;
    const hasTotp = factors.some((f) => f.factorId === TOTP_FACTOR_ID);
    const user = auth.currentUser;
    const isPasswordUser = !!user?.providerData?.some((p) => p?.providerId === 'password');

    const reloadFactors = useCallback(() => setFactors(readFactors()), []);

    const syncWithBackend = useCallback(async () => {
        const res = await apiService.post('/auth/mfa/sync', {});
        if (res?.mfa) {
            setInfo(res.mfa);
            onMfaChange?.(res.mfa);
        }
        return res?.mfa || null;
    }, [onMfaChange]);

    // Self-heal: Firebase is the truth for what is enrolled. If the backend's
    // record disagrees (a sync that failed after an enrol), re-sync once.
    const healedRef = useRef(false);
    useEffect(() => {
        if (healedRef.current || !info || typeof info.enabled !== 'boolean') return;
        if (info.enabled === (factors.length > 0)) return;
        healedRef.current = true;
        syncWithBackend().catch(() => { /* next open retries */ });
    }, [info, factors, syncWithBackend]);

    const handleError = async (err, retry) => {
        if (err?.code === 'auth/requires-recent-login') {
            retryRef.current = retry;
            setReauthPassword('');
            setReauthError(null);
            setReauthOpen(true);
            return;
        }
        if (err?.code === 'auth/unverified-email') {
            try { await sendEmailVerification(auth.currentUser); } catch (_) { /* the notice still applies */ }
            setWizard(null);
            setNotice('verify-email');
            return;
        }
        console.error('2FA settings action failed:', err);
        setError(err?.status ? userErrorMessage(err, t) : mfaErrorMessage(err, t));
    };

    const begin = () => { setError(null); setNotice(null); };

    // The step after the secret: a FIRST method needs the recovery codes
    // acknowledged; one added beside an existing (legacy) factor keeps the set.
    const afterSecret = async (w) => (enabled
        ? { ...w, step: 'code' }
        : { ...w, step: 'codes', codes: await fetchRecoveryCodes() });

    const startTotp = async () => {
        begin();
        setBusy(true);
        try {
            const session = await multiFactor(auth.currentUser).getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            setEnrollCode('');
            setWizard({ kind: 'totp', step: 'secret', secret });
        } catch (err) {
            await handleError(err, startTotp);
        } finally {
            setBusy(false);
        }
    };

    const totpNext = async () => {
        const w = wizard;
        begin();
        setBusy(true);
        try {
            setWizard(await afterSecret(w));
        } catch (err) {
            await handleError(err, totpNext);
        } finally {
            setBusy(false);
        }
    };

    const codesAcknowledged = () => {
        if (wizard?.kind === 'regen') {
            setInfo((prev) => ({ ...(prev || {}), enabled: true, recovery_codes_remaining: wizard.codes.length }));
            setWizard(null);
            setNotice('codes-saved');
            return;
        }
        setWizard({ ...wizard, step: 'code' });
    };

    const submitEnroll = async (e) => {
        e?.preventDefault?.();
        const w = wizard;
        if (!w || enrollCode.length !== 6 || busy) return;
        begin();
        setBusy(true);
        try {
            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(w.secret, enrollCode);
            await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator app');
        } catch (err) {
            setBusy(false);
            await handleError(err, () => submitEnroll());
            return;
        }
        // Enrolled. The new ID token carries the second factor; store it before
        // the sync so no request of ours goes out with the single-factor one.
        await tryRefreshFirebaseToken();
        try {
            await syncWithBackend();
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
        reloadFactors();
        setWizard(null);
        setEnrollCode('');
        setNotice('enabled');
        setBusy(false);
    };

    const regenerate = async () => {
        begin();
        setBusy(true);
        try {
            const codes = await fetchRecoveryCodes();
            setWizard({ kind: 'regen', step: 'codes', codes });
        } catch (err) {
            await handleError(err, regenerate);
        } finally {
            setBusy(false);
        }
    };

    const removeFactor = async (factor) => {
        begin();
        setBusy(true);
        try {
            await multiFactor(auth.currentUser).unenroll(factor);
        } catch (err) {
            setBusy(false);
            await handleError(err, () => removeFactor(factor));
            return;
        }
        // Firebase revokes the other sessions on unenrol; keep THIS one alive.
        await tryRefreshFirebaseToken();
        try {
            await syncWithBackend();
        } catch (err) {
            setError(userErrorMessage(err, t));
        }
        reloadFactors();
        setNotice('removed');
        setBusy(false);
    };

    const completeReauth = () => {
        const retry = retryRef.current;
        retryRef.current = null;
        setChallenge(null);
        setReauthOpen(false);
        setReauthPassword('');
        if (retry) retry();
    };

    const cancelReauth = () => {
        retryRef.current = null;
        setChallenge(null);
        setReauthOpen(false);
        setReauthPassword('');
        setReauthError(null);
    };

    const submitReauth = async (e) => {
        e?.preventDefault?.();
        const u = auth.currentUser;
        if (!u || busy) return;
        if (isPasswordUser && !reauthPassword) return;
        setReauthError(null);
        setBusy(true);
        try {
            if (isPasswordUser) {
                await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, reauthPassword));
            } else {
                await reauthenticateWithPopup(u, googleProvider);
            }
            setBusy(false);
            completeReauth();
        } catch (err) {
            setBusy(false);
            if (err?.code === 'auth/multi-factor-auth-required') {
                setChallenge(getMultiFactorResolver(auth, err));
                return;
            }
            setReauthError(mfaErrorMessage(err, t));
        }
    };

    const cancelWizard = () => {
        setWizard(null);
        setEnrollCode('');
        setError(null);
    };

    const statusText = enabled
        ? t('mfaStatusOn', 'On. Every sign-in asks for a code from your second step.')
        : t('mfaStatusOff', 'Off. Add a second step to sign-in so a stolen password is not enough.');

    const renderWizard = () => {
        if (!wizard) return null;
        const cancel = (
            <button type="button" data-testid="mfa-wizard-cancel" onClick={cancelWizard} className={BTN}>
                {t('commonCancel', 'Cancel')}
            </button>
        );
        if (wizard.step === 'codes') {
            return (
                <div className={PANEL} data-testid="mfa-wizard" data-step="codes" data-kind={wizard.kind}>
                    <RecoveryCodesPanel codes={wizard.codes} onContinue={codesAcknowledged} busy={busy} />
                </div>
            );
        }
        if (wizard.step === 'secret') {
            const email = auth.currentUser?.email || '';
            return (
                <div className={PANEL} data-testid="mfa-wizard" data-step="secret" data-kind="totp">
                    <div>
                        <div className={ROW_LABEL}>{t('mfaTotpScanTitle', 'Scan with your authenticator app')}</div>
                        <div className={ROW_DESC}>
                            {t('mfaTotpScanIntro', 'Open Google Authenticator, 1Password, Authy or a similar app and scan this code. Or type the key below into the app.')}
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <QRCode
                            data-testid="mfa-totp-qr"
                            value={wizard.secret.generateQrCodeUrl(email, 'PAI')}
                            size={184}
                            title={t('mfaTotpQrAlt', 'QR code for your authenticator app')}
                        />
                    </div>
                    <div className="text-center">
                        <div className="text-[12px] text-pai-meta">{t('mfaTotpKeyLabel', 'Setup key')}</div>
                        <code
                            data-testid="mfa-totp-secret"
                            translate="no"
                            className="mt-1 inline-block break-all select-all font-mono text-[14px] tracking-wider text-white"
                        >{wizard.secret.secretKey}</code>
                    </div>
                    <div className="flex justify-end gap-2">
                        {cancel}
                        <button type="button" data-testid="mfa-totp-next" onClick={totpNext} disabled={busy} className={BTN_PRIMARY}>
                            {t('commonNext', 'Next')}
                        </button>
                    </div>
                </div>
            );
        }
        // step === 'code'
        return (
            <form className={PANEL} data-testid="mfa-wizard" data-step="code" data-kind={wizard.kind} onSubmit={submitEnroll}>
                <div>
                    <label htmlFor="mfa-enroll-code" className={ROW_LABEL}>{t('mfaEnrollCodeLabel', 'Enter the 6-digit code')}</label>
                    <div className={ROW_DESC}>
                        {t('mfaEnrollCodeTotp', 'Your authenticator app now shows a code for PAI.')}
                    </div>
                </div>
                <input
                    id="mfa-enroll-code"
                    data-testid="mfa-enroll-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={enrollCode}
                    onChange={(e) => setEnrollCode(sanitizeCode(e.target.value))}
                    className={`${INPUT} font-mono tracking-[0.3em]`}
                />
                <div className="flex justify-end gap-2">
                    {cancel}
                    <button type="submit" data-testid="mfa-enroll-submit" disabled={busy || enrollCode.length !== 6} className={BTN_PRIMARY}>
                        {t('mfaActivate', 'Activate')}
                    </button>
                </div>
            </form>
        );
    };

    const renderReauth = () => {
        if (challenge) {
            return (
                <div className={PANEL} data-testid="mfa-reauth-challenge">
                    <MfaChallenge
                        resolver={challenge}
                        allowRecovery={false}
                        onResolved={completeReauth}
                        onCancel={cancelReauth}
                    />
                </div>
            );
        }
        if (!reauthOpen) return null;
        return (
            <form className={PANEL} data-testid="mfa-reauth" onSubmit={submitReauth}>
                <div>
                    <div className={ROW_LABEL}>{t('mfaReauthTitle', 'Confirm it is you')}</div>
                    <div className={ROW_DESC}>
                        {isPasswordUser
                            ? t('mfaReauthPasswordIntro', 'For your security, enter your password to continue.')
                            : t('mfaReauthGoogleIntro', 'For your security, sign in with Google again to continue.')}
                    </div>
                </div>
                {isPasswordUser && (
                    <input
                        data-testid="mfa-reauth-password"
                        type="password"
                        autoComplete="current-password"
                        aria-label={t('commonPassword', 'Password')}
                        value={reauthPassword}
                        onChange={(e) => setReauthPassword(e.target.value)}
                        className={INPUT}
                    />
                )}
                {reauthError && (
                    <div data-testid="mfa-reauth-error" role="alert" className="text-[12.5px] text-pai-red">{reauthError}</div>
                )}
                <div className="flex justify-end gap-2">
                    <button type="button" data-testid="mfa-reauth-cancel" onClick={cancelReauth} className={BTN}>
                        {t('commonCancel', 'Cancel')}
                    </button>
                    <button
                        type="submit"
                        data-testid="mfa-reauth-submit"
                        disabled={busy || (isPasswordUser && !reauthPassword)}
                        className={BTN_PRIMARY}
                    >
                        {isPasswordUser ? t('mfaContinue', 'Continue') : t('auxLoginContinueGoogle', 'Continue with Google')}
                    </button>
                </div>
            </form>
        );
    };

    const idle = !wizard && !reauthOpen && !challenge;

    return (
        <div data-testid="settings-security">
            <h3 className={EYEBROW}>{t('mfaSecuritySection', 'Security')}</h3>
            <div className={GROUP_CARD}>
                <div className={ROW}>
                    <div className={ROW_MAIN}>
                        <ShieldCheck size={16} className={`${enabled ? 'text-pai-green' : 'text-pai-meta'} flex-shrink-0`} />
                        <div className="min-w-0">
                            <div className={ROW_LABEL}>{t('mfaTitle', 'Two-step verification')}</div>
                            <div className={ROW_DESC} data-testid="mfa-status" data-enabled={enabled ? 'true' : 'false'}>
                                {statusText}
                            </div>
                        </div>
                    </div>
                </div>

                {factors.map((f) => (
                    <div key={f.uid} className={ROW} data-testid="mfa-factor-row" data-factor={f.factorId}>
                        <div className={ROW_MAIN}>
                            {/* A legacy phone factor (SMS was removed, PO 2026-10-28) is
                                still listed so it can be removed; none exist in practice. */}
                            {f.factorId === PHONE_FACTOR_ID
                                ? <Smartphone size={16} className="text-pai-meta flex-shrink-0" />
                                : <KeyRound size={16} className="text-pai-meta flex-shrink-0" />}
                            <div className="min-w-0">
                                <div className={ROW_LABEL}>{factorLabel(f, t)}</div>
                                {f.enrollmentTime && (
                                    <div className={ROW_DESC}>
                                        {t('mfaFactorAdded', 'Added {date}', { date: new Date(f.enrollmentTime).toLocaleDateString() })}
                                    </div>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="mfa-factor-remove"
                            onClick={() => removeFactor(f)}
                            disabled={busy || !idle}
                            className={BTN}
                        >
                            {t('commonRemove', 'Remove')}
                        </button>
                    </div>
                ))}

                {enabled && (
                    <div className={ROW}>
                        <div className="min-w-0">
                            <div className={ROW_LABEL}>{t('mfaRecoveryCodesTitle', 'Recovery codes')}</div>
                            <div className={ROW_DESC} data-testid="mfa-codes-remaining" data-count={info?.recovery_codes_remaining ?? ''}>
                                {t('mfaCodesRemaining', '{n} of 10 left', { n: info?.recovery_codes_remaining ?? 0 })}
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="mfa-regenerate-codes"
                            onClick={regenerate}
                            disabled={busy || !idle}
                            className={BTN}
                        >
                            {t('mfaRegenerateCodes', 'Generate new recovery codes')}
                        </button>
                    </div>
                )}

                {renderReauth()}
                {!reauthOpen && !challenge && renderWizard()}

                {/* Authenticator app only (SMS removed, PO 2026-10-28): once one is
                    enrolled there is nothing left to add. */}
                {idle && !hasTotp && (
                    <div className={`${ROW} flex-wrap`}>
                        <div className="min-w-0">
                            <div className={ROW_LABEL}>
                                {enabled ? t('mfaAddTotp', 'Add an authenticator app') : t('mfaSetUp', 'Set up two-step verification')}
                            </div>
                            <div className={ROW_DESC}>
                                {t('mfaMethodsHint', 'Use an authenticator app such as Google Authenticator, 1Password or Authy. Your recovery codes are the backup.')}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button type="button" data-testid="mfa-enable-totp" onClick={startTotp} disabled={busy} className={BTN}>
                                {t('mfaEnableTotp', 'Set up authenticator app')}
                            </button>
                        </div>
                    </div>
                )}

                {(notice || error) && (
                    <div className={PANEL}>
                        {notice === 'enabled' && (
                            <div data-testid="mfa-enabled-notice" role="status" className="text-[13px] font-semibold text-pai-green">
                                {t('mfaEnabledNotice', 'Two-step verification is on. Your other devices have to sign in again.')}
                            </div>
                        )}
                        {notice === 'removed' && (
                            <div data-testid="mfa-removed-notice" role="status" className="text-[13px] font-semibold text-pai-green">
                                {t('mfaRemovedNotice', 'Method removed.')}
                            </div>
                        )}
                        {notice === 'codes-saved' && (
                            <div data-testid="mfa-codes-saved-notice" role="status" className="text-[13px] font-semibold text-pai-green">
                                {t('mfaCodesSavedNotice', 'New recovery codes saved. The old ones no longer work.')}
                            </div>
                        )}
                        {notice === 'verify-email' && (
                            <div data-testid="mfa-verify-email-notice" role="status" className="text-[13px] text-pai-amber">
                                {t('mfaVerifyEmailNotice', 'Verify your email first — we sent you a link.')}
                            </div>
                        )}
                        {error && (
                            <div data-testid="mfa-settings-error" role="alert" className="text-[12.5px] text-pai-red">{error}</div>
                        )}
                    </div>
                )}

                <div className={PANEL}>
                    <p data-testid="mfa-messaging-note" className="text-[12.5px] text-pai-meta">
                        {t('mfaMessagingNote', 'Messaging links (Telegram) can only be changed from Settings.')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SecuritySection;
